import { useCallback, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { useFoldResource } from '../hooks/useFoldResource';
import { memberReportsAPI } from '../services/api';
import { PageHeader, PageState, StaleNotice } from './founder-dashboard/onboarding/outreach/OutreachShared';
import { focusRing } from './founder-dashboard/onboarding/outreach/outreachUtils';
import { readRequestError } from './runtimeLimitsForm';
import {
  RESOLUTIONS,
  RESOLUTION_NOTE_MAX,
  REVIEW_TARGET_HOURS,
  STATUS_FILTERS,
  checkResolution,
  formatCentral,
  isOverdue,
  kindLabel,
  reasonLabel,
  resolutionLabel,
  statusFilterFrom,
  statusLabel,
  waitingLabel,
} from './memberReports';

const PAGE_SIZE = 50;
const HEADER_CELL = 'px-4 py-3 font-semibold';
const CELL = 'px-4 py-3 align-top';
const SECONDARY = 'mt-0.5 text-xs text-gray-600';
const LINK = `rounded text-blue-700 underline-offset-2 hover:underline ${focusRing}`;

const STATUS_TONES = {
  open: 'border-blue-200 bg-blue-50 text-blue-800',
  reviewing: 'border-amber-200 bg-amber-50 text-amber-900',
  resolved: 'border-green-200 bg-green-50 text-green-800',
};

function actionErrorMessage(error, fallback) {
  const failure = readRequestError(error, fallback);
  return failure.type === 'no_response' ? 'The server did not respond. Check your connection and try again.' : failure.message;
}

export default function MemberReportsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = statusFilterFrom(searchParams.toString());
  const page = Math.max(0, Number.parseInt(searchParams.get('page') ?? '0', 10) || 0);

  const requestQueue = useCallback(
    () =>
      memberReportsAPI
        .list({ status: filter, limit: PAGE_SIZE, offset: page * PAGE_SIZE })
        .then((response) => ({ ...response.data, filter, page })),
    [filter, page],
  );
  const [queue, loadQueue] = useFoldResource(requestQueue, 'Member reports could not be loaded.');
  const current = queue.status === 'ready' && queue.payload.filter === filter && queue.payload.page === page;
  const data = current ? queue.payload : null;
  const failure = queue.status === 'failed' ? queue.message : queue.status === 'ready' && !current ? queue.refreshError : null;

  const showPage = (nextPage) => {
    const next = new URLSearchParams(searchParams);
    if (nextPage > 0) next.set('page', String(nextPage));
    else next.delete('page');
    setSearchParams(next);
  };
  const chooseFilter = (value) => setSearchParams(value === 'unresolved' ? {} : { status: value });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Member Reports"
        description={`Reports members filed about content or people in their church, oldest first. Aim to resolve each within ${REVIEW_TARGET_HOURS} hours. The reported member never sees a report.`}
        refreshing={Boolean(queue.refreshing)}
        onRefresh={loadQueue}
      />
      {queue.status === 'ready' ? <StaleNotice loadedAt={queue.loadedAt} /> : null}

      <div role="group" aria-label="Report status" className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map((option) => (
          <button
            key={option.value}
            type="button"
            aria-pressed={option.value === filter}
            onClick={() => chooseFilter(option.value)}
            className={`min-h-touch rounded-full border px-4 py-2 text-sm font-medium transition-colors ${focusRing} ${
              option.value === filter
                ? 'border-deep-navy bg-deep-navy text-white shadow-sm'
                : 'border-gray-200 bg-white text-gray-600 hover:border-sky-blue hover:bg-gray-50 hover:text-deep-navy'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {queue.status === 'denied' ? (
        <AccessRequired />
      ) : failure ? (
        <LoadFailed title="Member reports could not be loaded" message={failure} onRetry={loadQueue} />
      ) : (
        <PageState loading={!data}>
          {data ? (
            <>
              {queue.refreshError ? (
                <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                  Member reports could not be refreshed: {queue.refreshError} Showing the last loaded list.
                </div>
              ) : null}
              <QueueCounts counts={data.counts} />
              <QueueTable reports={data.reports} filter={filter} />
              <Pager page={page} shown={data.reports.length} total={data.total} onPage={showPage} />
            </>
          ) : null}
        </PageState>
      )}
    </div>
  );
}

function QueueCounts({ counts }) {
  const items = [
    { label: 'Open', value: counts.open },
    { label: 'In review', value: counts.reviewing },
    { label: `Over ${REVIEW_TARGET_HOURS} hours`, value: counts.overdue, alert: counts.overdue > 0 },
  ];
  return (
    <dl className="grid gap-3 sm:grid-cols-3">
      {items.map((item) => (
        <div
          key={item.label}
          className={`rounded-xl border px-4 py-3 shadow-sm ${
            item.alert ? 'border-red-200 bg-red-50 text-red-900' : 'border-gray-200 bg-white text-deep-navy'
          }`}
        >
          <dt className="text-sm font-semibold">{item.label}</dt>
          <dd className="mt-1 text-2xl font-bold tabular-nums">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function StatusPill({ status }) {
  return (
    <span
      className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${
        STATUS_TONES[status] ?? 'border-gray-300 bg-gray-100 text-gray-800'
      }`}
    >
      {statusLabel(status)}
    </span>
  );
}

function OverduePill() {
  return (
    <span className="inline-flex rounded-full border border-red-300 bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-800">
      Overdue
    </span>
  );
}

const EMPTY_COPY = {
  unresolved: 'No reports need review.',
  open: 'No open reports.',
  reviewing: 'No reports are in review.',
  resolved: 'No resolved reports yet.',
  all: 'No member has filed a report yet.',
};

function QueueTable({ reports, filter }) {
  if (reports.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-600">
        {EMPTY_COPY[filter]}
      </p>
    );
  }
  const now = Date.now();
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <caption className="sr-only">Member reports, oldest first</caption>
        <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-600">
          <tr>
            <th scope="col" className={HEADER_CELL}>Filed</th>
            <th scope="col" className={HEADER_CELL}>Status</th>
            <th scope="col" className={HEADER_CELL}>Report</th>
            <th scope="col" className={HEADER_CELL}>Reason</th>
            <th scope="col" className={HEADER_CELL}>Church</th>
            <th scope="col" className={HEADER_CELL}>Reported by</th>
            <th scope="col" className={HEADER_CELL}>Reported member</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {reports.map((report) => {
            const overdue = isOverdue(report, now);
            return (
              <tr key={report.id} className={overdue ? 'bg-red-50/40' : undefined}>
                <td className={`${CELL} whitespace-nowrap text-gray-900`}>
                  {formatCentral(report.createdAt)}
                  {report.status === 'resolved' ? null : (
                    <p className={SECONDARY}>Waiting {waitingLabel(report.createdAt, now)}</p>
                  )}
                  {overdue ? (
                    <p className="mt-1">
                      <OverduePill />
                    </p>
                  ) : null}
                </td>
                <td className={CELL}>
                  <StatusPill status={report.status} />
                </td>
                <th scope="row" className={`${CELL} max-w-sm text-left font-medium`}>
                  <Link to={`/member-reports/${report.id}`} className={LINK}>
                    {kindLabel(report.targetKind)}
                  </Link>
                  {report.snippet ? (
                    <p className={`${SECONDARY} line-clamp-2 font-normal`}>&ldquo;{report.snippet}&rdquo;</p>
                  ) : null}
                </th>
                <td className={`${CELL} text-gray-900`}>{reasonLabel(report.reason)}</td>
                <td className={`${CELL} text-gray-900`}>
                  {report.organization.name}
                  {report.group ? <p className={SECONDARY}>{report.group.name}</p> : null}
                </td>
                <td className={`${CELL} text-gray-900`}>{report.reporter?.name ?? 'Deleted account'}</td>
                <td className={`${CELL} text-gray-900`}>{report.reportedUser?.name ?? 'Unknown'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Pager({ page, shown, total, onPage }) {
  if (total <= PAGE_SIZE && page === 0) return null;
  const first = page * PAGE_SIZE + 1;
  const last = page * PAGE_SIZE + shown;
  const button = `min-h-[44px] rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 ${focusRing}`;
  return (
    <div className="flex items-center justify-between gap-3 text-sm text-gray-600">
      <p>
        {shown > 0 ? `${first}–${last}` : 0} of {total}
      </p>
      <div className="flex gap-2">
        <button type="button" className={button} disabled={page === 0} onClick={() => onPage(page - 1)}>
          Previous
        </button>
        <button type="button" className={button} disabled={last >= total} onClick={() => onPage(page + 1)}>
          Next
        </button>
      </div>
    </div>
  );
}

function AccessRequired() {
  return (
    <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-6 text-amber-900">
      <LockClosedIcon className="mt-0.5 h-6 w-6 flex-none" />
      <p>Fold access is required to see member reports.</p>
    </div>
  );
}

function LoadFailed({ title, message, onRetry }) {
  return (
    <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-900">
      <p className="font-semibold">{title}</p>
      <p className="mt-1 text-sm">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className={`mt-4 min-h-[44px] rounded-lg border border-current px-4 py-2 text-sm font-semibold ${focusRing}`}
      >
        Try again
      </button>
    </div>
  );
}

export function MemberReportPage() {
  const { id } = useParams();
  const requestReport = useCallback(
    () => memberReportsAPI.get(id).then((response) => response.data.report),
    [id],
  );
  const [resource, loadReport] = useFoldResource(requestReport, 'This report could not be loaded.');
  const report = resource.status === 'ready' ? resource.payload : null;

  return (
    <div className="space-y-6">
      <Link to="/member-reports" className={`inline-flex items-center gap-2 text-sm font-medium ${LINK}`}>
        <ArrowLeftIcon className="h-4 w-4" />
        Member Reports
      </Link>
      {resource.status === 'denied' ? (
        <AccessRequired />
      ) : resource.status === 'failed' ? (
        <LoadFailed title="This report could not be loaded" message={resource.message} onRetry={loadReport} />
      ) : (
        <PageState loading={resource.status === 'loading'}>
          {report ? <ReportDetail report={report} onChanged={loadReport} refreshError={resource.refreshError} /> : null}
        </PageState>
      )}
    </div>
  );
}

function ReportDetail({ report, onChanged, refreshError }) {
  const overdue = isOverdue(report);
  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="text-xl font-semibold text-gray-900">{kindLabel(report.targetKind)} report</h3>
        <StatusPill status={report.status} />
        {overdue ? <OverduePill /> : null}
      </div>
      <p className="text-sm text-gray-600">
        Filed {formatCentral(report.createdAt)}
        {report.status === 'resolved' ? '' : `, waiting ${waitingLabel(report.createdAt)}`}. Reason: {reasonLabel(report.reason)}.
      </p>
      {refreshError ? (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          This report could not be refreshed: {refreshError}
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="space-y-6">
          <SavedWords report={report} />
          {report.note ? (
            <Card title="Reporter's note">
              <p className="whitespace-pre-wrap text-sm text-gray-900">{report.note}</p>
            </Card>
          ) : null}
          <ReviewPanel report={report} onChanged={onChanged} />
        </div>
        <div className="space-y-6">
          <People report={report} />
          <Where report={report} />
          <History report={report} />
        </div>
      </div>
    </>
  );
}

function Card({ title, children }) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <h4 className="font-semibold text-gray-900">{title}</h4>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function Fact({ label, children }) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-gray-900">{children}</dd>
    </div>
  );
}

function SavedWords({ report }) {
  const snapshot = report.snapshot ?? {};
  const text = snapshot.text || (snapshot.type ? `No text. This was a ${snapshot.type} message.` : 'No text was saved.');
  return (
    <Card title={report.targetKind === 'person' ? 'Reported person' : 'Saved words'}>
      <blockquote className="whitespace-pre-wrap rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-[15px] text-gray-900">
        {text}
      </blockquote>
      <p className="mt-2 text-xs text-gray-500">Saved when the report was filed. Later edits or deletions do not change it.</p>
      <dl className="mt-4 grid gap-3 sm:grid-cols-2">
        {snapshot.authorName ? (
          <Fact label="Written by">
            {snapshot.authorName}
            {snapshot.isAnonymous ? <span className="text-gray-600"> (posted anonymously)</span> : null}
          </Fact>
        ) : null}
        {snapshot.postedAt ? <Fact label="Posted">{formatCentral(snapshot.postedAt)}</Fact> : null}
        {snapshot.privacyLevel ? <Fact label="Privacy">{snapshot.privacyLevel}</Fact> : null}
        {snapshot.prayerText ? <Fact label="On the prayer request">{snapshot.prayerText}</Fact> : null}
        {snapshot.announcementText ? <Fact label="On the announcement">{snapshot.announcementText}</Fact> : null}
      </dl>
    </Card>
  );
}

function PersonFact({ label, person, link, missing }) {
  return (
    <Fact label={label}>
      {person ? (
        <>
          {link ? (
            <Link to={link} className={LINK}>
              {person.name || person.email}
            </Link>
          ) : (
            person.name || person.email
          )}
          {person.email ? <p className={SECONDARY}>{person.email}</p> : null}
        </>
      ) : (
        <span className="text-gray-600">{missing}</span>
      )}
    </Fact>
  );
}

function People({ report }) {
  const others = report.otherReportsAgainstReportedUser ?? 0;
  return (
    <Card title="People">
      <dl className="space-y-3">
        <PersonFact label="Reported by" person={report.reporter} link={report.links?.reporter} missing="Deleted account" />
        <PersonFact
          label="Reported member"
          person={report.reportedUser}
          link={report.links?.reportedUser}
          missing="Unknown. The author was anonymous or the account was deleted."
        />
        {report.reportedUser ? (
          <p className={`text-sm ${others > 0 ? 'font-semibold text-red-800' : 'text-gray-600'}`}>
            {others === 0 ? 'No other reports about this member.' : `${others} other ${others === 1 ? 'report' : 'reports'} about this member.`}
          </p>
        ) : null}
      </dl>
    </Card>
  );
}

function Where({ report }) {
  return (
    <Card title="Where">
      <dl className="space-y-3">
        <Fact label="Church">
          <Link to={report.links?.organization ?? `/founder-dashboard/organizations/${report.organization.id}`} className={LINK}>
            {report.organization.name}
          </Link>
        </Fact>
        <Fact label="Group">
          {report.group ? (
            <Link to={report.links?.group ?? `/founder-dashboard/groups/${report.group.id}`} className={LINK}>
              {report.group.name}
            </Link>
          ) : (
            <span className="text-gray-600">Not in a group</span>
          )}
        </Fact>
      </dl>
    </Card>
  );
}

function History({ report }) {
  return (
    <Card title="History">
      <dl className="space-y-3">
        <Fact label="Filed">{formatCentral(report.createdAt)}</Fact>
        <Fact label="Review started">
          {report.reviewStartedAt
            ? `${formatCentral(report.reviewStartedAt)} by ${report.reviewer?.name ?? 'a former staff member'}`
            : 'Not yet'}
        </Fact>
        <Fact label="Resolved">
          {report.resolvedAt
            ? `${formatCentral(report.resolvedAt)} by ${report.resolver?.name ?? 'a former staff member'}`
            : 'Not yet'}
        </Fact>
      </dl>
    </Card>
  );
}

function ReviewPanel({ report, onChanged }) {
  if (report.status === 'resolved') {
    return (
      <Card title="Resolution">
        <p className="inline-flex rounded-full border border-green-200 bg-green-50 px-2.5 py-1 text-xs font-semibold text-green-800">
          {resolutionLabel(report.resolution)}
        </p>
        <p className="mt-3 whitespace-pre-wrap text-sm text-gray-900">{report.resolutionNote}</p>
      </Card>
    );
  }
  return <ReviewActions report={report} onChanged={onChanged} />;
}

function ReviewActions({ report, onChanged }) {
  const [draft, setDraft] = useState({ resolution: '', note: '' });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(null);
  const [actionError, setActionError] = useState(null);

  const run = async (kind, call, fallback) => {
    setBusy(kind);
    setActionError(null);
    try {
      await call();
      await onChanged();
    } catch (error) {
      setActionError(actionErrorMessage(error, fallback));
    } finally {
      setBusy(null);
    }
  };

  const startReview = () =>
    run('review', () => memberReportsAPI.startReview(report.id), 'The review could not be started.');

  const resolve = (event) => {
    event.preventDefault();
    const check = checkResolution(draft);
    if (!check.ok) {
      setErrors(check.errors);
      return;
    }
    setErrors({});
    run('resolve', () => memberReportsAPI.resolve(report.id, check.body), 'The report could not be resolved.');
  };

  const edit = (changes) => {
    setDraft((current) => ({ ...current, ...changes }));
    setErrors((current) => {
      const next = { ...current };
      for (const key of Object.keys(changes)) delete next[key];
      return next;
    });
  };

  return (
    <Card title="Review">
      {actionError ? (
        <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {actionError}
        </div>
      ) : null}

      {report.status === 'open' ? (
        <div className="mb-5 flex flex-col gap-2 border-b border-gray-100 pb-5 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-gray-600">Start a review so other staff know you have this one.</p>
          <button
            type="button"
            onClick={startReview}
            disabled={busy !== null}
            className={`min-h-[44px] rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-60 ${focusRing}`}
          >
            {busy === 'review' ? 'Starting…' : 'Start review'}
          </button>
        </div>
      ) : (
        <p className="mb-5 border-b border-gray-100 pb-5 text-sm text-gray-600">
          In review by {report.reviewer?.name ?? 'a former staff member'} since {formatCentral(report.reviewStartedAt)}.
        </p>
      )}

      <form onSubmit={resolve} noValidate className="space-y-4">
        <fieldset aria-describedby={errors.resolution ? 'report-resolution-error' : undefined}>
          <legend className="text-sm font-semibold text-gray-800">Resolution</legend>
          <p className="mt-1 text-xs text-gray-500">
            Recording a resolution does not change anything in the app. Take the action first, for example in User Management.
          </p>
          <div className="mt-2 space-y-2">
            {RESOLUTIONS.map((option) => (
              <label
                key={option.value}
                className={`flex min-h-[44px] cursor-pointer items-start gap-3 rounded-lg border px-3 py-2 text-sm ${
                  draft.resolution === option.value ? 'border-storm-blue bg-blue-50' : 'border-gray-200 bg-white'
                }`}
              >
                <input
                  type="radio"
                  name="resolution"
                  value={option.value}
                  checked={draft.resolution === option.value}
                  disabled={busy !== null}
                  onChange={() => edit({ resolution: option.value })}
                  className={`mt-0.5 h-4 w-4 flex-none ${focusRing}`}
                />
                <span>
                  <span className="font-semibold text-gray-900">{option.label}</span>
                  <span className="block text-xs text-gray-600">{option.detail}</span>
                </span>
              </label>
            ))}
          </div>
          {errors.resolution ? (
            <p id="report-resolution-error" className="mt-2 text-sm font-medium text-red-700">
              {errors.resolution}
            </p>
          ) : null}
        </fieldset>

        <div>
          <label htmlFor="report-resolution-note" className="block text-sm font-semibold text-gray-800">
            Note (required)
          </label>
          <textarea
            id="report-resolution-note"
            rows={3}
            required
            maxLength={RESOLUTION_NOTE_MAX}
            value={draft.note}
            disabled={busy !== null}
            aria-invalid={errors.note ? true : undefined}
            aria-describedby={errors.note ? 'report-resolution-note-error' : 'report-resolution-note-help'}
            onChange={(event) => edit({ note: event.target.value })}
            className={`mt-2 w-full rounded-lg border px-3 py-2 text-sm text-gray-900 ${
              errors.note ? 'border-red-400' : 'border-gray-300'
            } ${focusRing}`}
          />
          {errors.note ? (
            <p id="report-resolution-note-error" className="mt-1 text-sm font-medium text-red-700">
              {errors.note}
            </p>
          ) : (
            <p id="report-resolution-note-help" className="mt-1 text-xs text-gray-500">
              What you checked and what you did. Saved with the report and the audit log.
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={busy !== null}
          className={`min-h-[44px] rounded-xl bg-deep-navy px-5 py-2 text-sm font-semibold text-white hover:bg-[#1B263B] disabled:opacity-60 ${focusRing}`}
        >
          {busy === 'resolve' ? 'Resolving…' : 'Resolve report'}
        </button>
      </form>
    </Card>
  );
}
